<template>
  <m-card-item
    icon="download"
    :disabled="disabled"
    @click="showDialog = true"
  >
    {{ $t('exportMenu.export') }}
  </m-card-item>
  <m-dialog
    v-model:value="showDialog"
    :title="$t('exportMenu.exportFormat')"
    hide-header
    :ok-text="$t('exportMenu.export')"
    :max-width="$modalSizes.sm"
    @ok="handleExport"
    @cancel="showDialog = false"
  >
    <m-form-item
      :label="$t('exportMenu.selectExportFormat')"
      class="_form-item"
    >
      <m-select
        v-model:value="selectedFormat"
        :items="exportFormatOptions"
        match-trigger-width
        full-width
      />
    </m-form-item>
    <m-form-item
      v-if="showWidthSelector"
      :label="$t('reportEditor.width')"
      class="_form-item"
    >
      <m-select
        v-model:value="selectedWidth"
        :items="printableWidthOptions"
        match-trigger-width
        full-width
      />
    </m-form-item>
  </m-dialog>
</template>

<script setup>
import useOverlaySpinner from '@/composables/overlay-spinner';
import usePageExport from '@/composables/export/page-export';
import usePersistedGoalCycle from '@/composables/goal-cycle/persisted-goal-cycle';
import useSnackBar from '@/composables/snackbar';
import { GOAL_EXPORT_LIMIT, PRINTABLE_WIDTH_NARROW, PRINTABLE_WIDTH_STANDARD, PRINTABLE_WIDTH_WIDE, UNASSIGNED } from '@/lib/constants';
import { GOAL_SELF } from '@/lib/props/custom-types';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import { computed, ref } from 'vue';
import { doCsvExport } from '@/api';
import { downloadFromBlob } from 'shared/lib/file-download';
import { logCatch } from '@/lib/logger/logger';
import { printableExportFormat, printablePage } from 'shared/constants.json';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const snackBar = useSnackBar();
const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner();
const { composedSelectedCycles } = usePersistedGoalCycle();

const props = defineProps({
  // only pdf/png related props:
  page: {
    type: String,
    default: undefined,
  },
  gridPageId: {
    type: Number,
    default: undefined,
  },
  spaceId: {
    type: Number,
    default: undefined,
  },
  planningId: {
    type: Number,
    default: undefined,
  },
  goalIds: {
    type: Array,
    default: () => [],
  },
  // both pdf/png and csv related props:
  allowedExportFormats: {
    type: Array,
    required: true,
  },
  savedView: {
    type: Object,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['export-requested']);

const { exportPage } = usePageExport();

const showDialog = ref(false);

const selectedFormat = ref(props.allowedExportFormats[0]);
const defaultWidths = {
  [printablePage.goalDetails]: PRINTABLE_WIDTH_NARROW,
  [printablePage.planningDetails]: PRINTABLE_WIDTH_WIDE,
  [printablePage.dashboard]: PRINTABLE_WIDTH_STANDARD,
  [printablePage.spacesGoals]: PRINTABLE_WIDTH_WIDE,
  [printablePage.spacesDashboard]: PRINTABLE_WIDTH_STANDARD,
  [printablePage.homeGoals]: PRINTABLE_WIDTH_WIDE,
  [printablePage.goalsExplorer]: PRINTABLE_WIDTH_WIDE,
  [printablePage.progressDashboard]: PRINTABLE_WIDTH_STANDARD,
  [printablePage.healthDashboard]: PRINTABLE_WIDTH_STANDARD,
};
const defaultWidth = defaultWidths[props.page] ?? PRINTABLE_WIDTH_STANDARD;
const selectedWidth = ref(defaultWidth);
const exportFormatOptions = computed(() => props.allowedExportFormats.map((format) => ({
  text: t(`exportPage.exportFormat.${format}`),
  value: format,
})));
const printableWidthOptions = [
  { text: t('reportEditor.pdfWidthNarrow'), value: PRINTABLE_WIDTH_NARROW },
  { text: t('reportEditor.pdfWidthStandard'), value: PRINTABLE_WIDTH_STANDARD },
  { text: t('reportEditor.pdfWidthWide'), value: PRINTABLE_WIDTH_WIDE },
];
const showWidthSelector = computed(() => [printableExportFormat.pdf, printableExportFormat.image].includes(selectedFormat.value));

const handleExport = () => {
  if (props.goalIds && props.goalIds.length > GOAL_EXPORT_LIMIT) {
    snackBar.error(t('exportMenu.exportTooManyGoals', { limit: GOAL_EXPORT_LIMIT }));
    return;
  }

  showOverlaySpinner(t('general.exporting'));

  const exportHandlers = {
    [printableExportFormat.pdf]: exportPdfPng,
    [printableExportFormat.image]: exportPdfPng,
    [printableExportFormat.csv]: exportCsv,
  };

  const exportFn = exportHandlers[selectedFormat.value];
  if (!exportFn) {
    hideOverlaySpinner();
    throw new Error(`Unsupported export format: ${selectedFormat.value}`);
  }

  exportFn().finally(() => {
    hideOverlaySpinner();
  });

  showDialog.value = false;
  emit('export-requested');
};

const exportPdfPng = async () => {
  if (!props.page) {
    throw new Error('PDF/PNG page is required');
  }

  const printableData = {
    page: props.page,
    exportFormat: selectedFormat.value,
  };

  if (props.gridPageId) {
    printableData.gridPage = { uid: props.gridPageId };
  }

  if (props.spaceId) {
    printableData.space = { uid: props.spaceId };
  }

  if (props.planningId) {
    printableData.planning = { uid: props.planningId };
  }

  if (props.savedView && props.savedView.uid) {
    printableData.savedView = { uid: props.savedView.uid };
  }

  if (props.goalIds && props.goalIds.length > 0) {
    printableData.goals = props.goalIds.map((uid) => ({ uid }));
  }

  printableData.width = selectedWidth.value;

  await exportPage(printableData);
};

const CSV_FILENAME_MAX_LENGTH = 200;

function csvFilenameFromView(savedView, contentDisposition) {
  const timestamp = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
  const title = savedView?.title?.trim();
  if (title) {
    const sanitized = title
      .replace(/[/\\:*?"<>|]/g, '_')
      .replace(/_+/g, '_')
      .trim()
      .slice(0, CSV_FILENAME_MAX_LENGTH);
    if (sanitized) {
      return `${sanitized} ${timestamp}.csv`;
    }
  }
  if (contentDisposition?.includes('filename=')) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match?.[1]) {
      return match[1];
    }
  }
  return `Goals ${timestamp}.csv`;
}

const exportCsv = async () => {
  let savedView = props.savedView;
  if (props.page === printablePage.goalDetails) {
    if (props.goalIds.length === 0) {
      throw new Error('Goal IDs are required for CSV goal details page');
    }
    savedView = {
      params: {
        filter: {
          scope: {
            directProperty: {
              type: GOAL_SELF,
              edgeName: 'uid',
            },
            relations: props.goalIds.map((uid) => ({ uid })),
          },
        },
      },
    };
  }

  if (!savedView) {
    throw new Error('CSV saved view is required');
  }

  const filter = savedView.params?.filter ?? null;
  const handler = new GoalFilterHandler();
  const filterPayload = handler.Translate(filter);

  const applyGoalCycles = ![printablePage.goalDetails, printablePage.planningDetails].includes(props.page);
  let goalCycles = applyGoalCycles
    ? composedSelectedCycles.value.map((c) => String(c?.uid ?? c))
    : [];
  if (savedView.lockCycleOnView && applyGoalCycles) {
    const viewCycles = savedView.goalCycles ?? [];
    goalCycles = viewCycles.map((c) => String(c?.uid ?? c));
    const hasUnassigned = goalCycles.includes(UNASSIGNED);
    if (savedView.unassignedGoalCycleSelected && !hasUnassigned) {
      goalCycles.push(UNASSIGNED);
    }
  }

  try {
    const response = await doCsvExport({ filter: filterPayload, goalCycles, includeDraft: props.page === printablePage.planningDetails });
    const filename = csvFilenameFromView(props.savedView, response.headers['content-disposition']);
    downloadFromBlob(filename, new Blob([response.data]));
  } catch (e) {
    logCatch((err) => {
      if (err.status === 429) {
        snackBar.error(t('exportButton.tooManyRequests'));
        return;
      }
      snackBar.error();
    })(e);
  }
};
</script>

<style scoped lang="scss">
._form-item {
  &:last-of-type {
    margin-bottom: 0;
  }
}
</style>
